import { createFileRoute, Link } from "@tanstack/react-router";
import { JsonLd } from "@/components/json-ld";
import { canonicalLink, seo } from "@/utils/seo";
import { breadcrumbJsonLd } from "@/utils/structured-data";

export const Route = createFileRoute("/legal/privacy")({
  head: () => ({
    meta: [
      ...seo({
        title: "Privacy Policy - Setups by SZESE",
        description:
          "Read the Setups by SZESE privacy policy. Learn how we handle your data, cookies, and account information.",
        canonical: "https://setupsbyszese.com/legal/privacy",
      }),
    ],
    links: [canonicalLink("https://setupsbyszese.com/legal/privacy")],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <main className="min-h-screen bg-background p-6">
      <JsonLd
        data={breadcrumbJsonLd([
          { name: "Home", url: "https://setupsbyszese.com" },
          { name: "Privacy Policy", url: "https://setupsbyszese.com/legal/privacy" },
        ])}
      />
      <div className="mx-auto max-w-3xl">
        <div className="mb-8">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            &larr; Back to Setups by SZESE
          </Link>
        </div>

        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: March 15, 2026</p>

        <div className="prose prose-invert prose-sm max-w-none space-y-6 text-muted-foreground [&_h2]:text-foreground [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:text-foreground [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2 [&_strong]:text-foreground [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1">
          <p>
            Setups by SZESE (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is operated by SZESE
            E-Sport, the sim racing team of Sz&eacute;chenyi Istv&aacute;n University, Gy&#x151;r,
            Hungary. This Privacy Policy explains how we collect, use, and protect your personal
            information when you use our website at{" "}
            <a href="https://setupsbyszese.com">setupsbyszese.com</a> (the &quot;Service&quot;).
          </p>

          <h2>1. Information We Collect</h2>

          <h3>Account Information</h3>
          <p>
            When you create an account, we collect information provided by your authentication
            method:
          </p>
          <ul>
            <li>
              <strong>Google OAuth:</strong> Your name, email address, and profile picture as shared
              by Google.
            </li>
            <li>
              <strong>Email sign-up:</strong> Your email address and the display name you choose.
            </li>
          </ul>

          <h3>Profile Information</h3>
          <p>
            You may optionally provide additional profile information such as a bio, social media
            links, iRating, and license class. Driver profiles are publicly visible.
          </p>

          <h3>Usage Data</h3>
          <p>We automatically collect:</p>
          <ul>
            <li>Pages you visit and features you use</li>
            <li>Setup downloads (which setups, when)</li>
            <li>Device type, browser, and general location (country level)</li>
            <li>Error reports and performance metrics</li>
          </ul>

          <h3>Payment Information</h3>
          <p>
            Payments are processed by{" "}
            <a href="https://polar.sh" target="_blank" rel="noopener noreferrer">
              Polar
            </a>
            . We do not store your credit card details. Polar may collect your name, email, billing
            address, and tax ID for invoice purposes. See{" "}
            <a href="https://polar.sh/legal/privacy" target="_blank" rel="noopener noreferrer">
              Polar&apos;s Privacy Policy
            </a>{" "}
            for details.
          </p>

          <h2>2. How We Use Your Information</h2>
          <ul>
            <li>
              <strong>Provide the Service:</strong> Authenticate your account, manage subscriptions,
              deliver setup files, and enforce tier-based access.
            </li>
            <li>
              <strong>Improve the Service:</strong> Analyze usage patterns to improve features,
              content, and performance.
            </li>
            <li>
              <strong>Communicate:</strong> Send transactional emails (setup approvals, subscription
              changes) and optional notifications.
            </li>
            <li>
              <strong>Security:</strong> Detect abuse, enforce rate limits, and protect the
              platform.
            </li>
          </ul>

          <h2>3. Third-Party Services</h2>
          <p>We use the following third-party services that may process your data:</p>
          <ul>
            <li>
              <strong>Supabase</strong> (database &amp; authentication) &mdash; stores account data
              and setup files. Hosted in the EU.
            </li>
            <li>
              <strong>Polar</strong> (payments) &mdash; processes subscriptions and billing.
            </li>
            <li>
              <strong>PostHog</strong> (analytics) &mdash; tracks anonymized usage data. EU
              instance.
            </li>
            <li>
              <strong>Sentry</strong> (error tracking) &mdash; captures error reports to help us fix
              bugs.
            </li>
            <li>
              <strong>Vercel</strong> (hosting) &mdash; serves the website and handles server-side
              rendering.
            </li>
            <li>
              <strong>Resend</strong> (email) &mdash; delivers transactional notification emails.
            </li>
          </ul>

          <h2>4. Cookies &amp; Local Storage</h2>
          <p>We use:</p>
          <ul>
            <li>
              <strong>Authentication cookies:</strong> A session cookie to keep you signed in.
              Essential for the Service to function.
            </li>
            <li>
              <strong>Local storage:</strong> Stores your session token and user preferences
              client-side.
            </li>
            <li>
              <strong>Analytics cookies:</strong> PostHog sets a cookie to distinguish unique
              visitors. You can opt out via your browser settings.
            </li>
          </ul>
          <p>We do not use advertising cookies or sell your data to advertisers.</p>

          <h2>5. Data Retention</h2>
          <ul>
            <li>
              <strong>Account data:</strong> Retained as long as your account is active. When you
              delete your account, your profile and personal data are permanently removed. Setups
              uploaded as a driver are anonymized (driver reference set to null) but not deleted, as
              other users may have downloaded them.
            </li>
            <li>
              <strong>Download history:</strong> Retained for analytics and usage tracking. Not
              publicly visible.
            </li>
            <li>
              <strong>Payment records:</strong> Retained by Polar as required by tax and accounting
              regulations.
            </li>
          </ul>

          <h2>6. Your Rights</h2>
          <p>
            Under the EU General Data Protection Regulation (GDPR) and Hungarian data protection
            law, you have the right to:
          </p>
          <ul>
            <li>
              <strong>Access</strong> the personal data we hold about you.
            </li>
            <li>
              <strong>Rectify</strong> inaccurate data via your profile settings.
            </li>
            <li>
              <strong>Delete</strong> your account and associated data via Settings &gt; Danger
              Zone.
            </li>
            <li>
              <strong>Export</strong> your data &mdash; contact us and we will provide a copy.
            </li>
            <li>
              <strong>Object</strong> to processing or request restriction.
            </li>
          </ul>

          <h2>7. Data Security</h2>
          <p>
            We implement industry-standard security measures including encrypted connections
            (HTTPS), row-level security policies on all database tables, rate limiting on API
            endpoints, and secure authentication via Supabase Auth. Setup files are stored in
            private Supabase Storage buckets and served via time-limited signed URLs.
          </p>

          <h2>8. Children&apos;s Privacy</h2>
          <p>
            The Service is not directed at children under 16. We do not knowingly collect personal
            data from children. If you believe a child has provided us with personal information,
            please contact us and we will delete it.
          </p>

          <h2>9. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify registered users of
            material changes via email or an in-app notification. The &quot;Last updated&quot; date
            at the top reflects the most recent revision.
          </p>

          <h2>10. Contact</h2>
          <p>
            For privacy-related questions or to exercise your rights, contact us at{" "}
            <a href="mailto:support@setupsbyszese.com">support@setupsbyszese.com</a>.
          </p>
          <p>
            SZESE E-Sport
            <br />
            Sz&eacute;chenyi Istv&aacute;n University
            <br />
            Gy&#x151;r, Hungary
          </p>
        </div>
      </div>
    </main>
  );
}
